<script setup lang="ts">
import type { SplashAdPayload } from '~/types/splash-ad'
import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'

const props = defineProps<{
  ad: SplashAdPayload
}>()
const emit = defineEmits<{
  (e: 'close'): void
}>()

const config = useRuntimeConfig()

const FADE_OUT_MS = 180
const closeDelayMs = computed(() => {
  const value = Number(props.ad.duration)
  if (!Number.isFinite(value) || value <= 0)
    return 5000
  return Math.round(value * 1000)
})

const overlayStyle = computed(() => ({
  backgroundColor: props.ad.filling_color || '#0f172a',
}))

let closeTimer: ReturnType<typeof setTimeout> | null = null
let countdownTimer: ReturnType<typeof setInterval> | null = null
const remainingSeconds = ref(0)
const isClosing = ref(false)

function clearCloseTimer() {
  if (closeTimer) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
}

function clearCountdownTimer() {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

function closeOverlay() {
  if (isClosing.value)
    return
  isClosing.value = true
  clearCloseTimer()
  clearCountdownTimer()
  window.setTimeout(() => {
    emit('close')
  }, FADE_OUT_MS)
}

async function openAdLink() {
  const link = `${config.public.apiUrl}/splash/registerclick`

  try {
    const platform = Capacitor.getPlatform()
    if (platform === 'ios' || platform === 'android') {
      await Browser.open({ url: link })
    }
    else {
      window.open(link, '_blank', 'noopener,noreferrer')
    }
  }
  catch (error) {
    console.warn('Splash ad link open failed:', error)
  }
  finally {
    closeOverlay()
  }
}

onMounted(() => {
  const slug = String(props.ad.slug || '').trim()
  if (slug.length > 0) {
    void $fetch(`${config.public.apiUrl}/splash/registerview?slug=${encodeURIComponent(slug)}`, {
      method: 'POST',
    }).catch((error) => {
      console.warn('Failed to register splash view:', error)
    })
  }

  remainingSeconds.value = Math.max(1, Math.ceil(closeDelayMs.value / 1000))
  countdownTimer = setInterval(() => {
    remainingSeconds.value = Math.max(0, remainingSeconds.value - 1)
  }, 1000)
  closeTimer = setTimeout(closeOverlay, closeDelayMs.value)
})

onBeforeUnmount(() => {
  clearCloseTimer()
  clearCountdownTimer()
})
</script>

<template>
  <div
    class="fixed inset-0 z-[1000] flex items-center justify-center p-4 transition-opacity duration-150"
    :class="isClosing ? 'opacity-0' : 'opacity-100'"
    :style="overlayStyle"
  >
    <div class="relative h-[95vh] w-[95vw] max-w-[1200px]">
      <div class="absolute inset-x-0 top-0 z-10 flex items-start justify-between">
        <div class="pointer-events-none rounded-full border border-white/55 bg-black/45 px-3 py-1 text-xs font-semibold tracking-wide text-white/95">
          PUBLICITÉ
        </div>
        <span
          class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/80 bg-black/55 text-xs font-semibold text-white"
          aria-label="Temps restant"
        >
          {{ remainingSeconds }}
        </span>
      </div>

      <button
        type="button"
        class="mt-10 h-[calc(100%-2.5rem)] w-full overflow-hidden"
        aria-label="Ouvrir la promotion"
        @click="openAdLink"
      >
        <img
          :src="ad.file"
          alt="Promotion"
          class="block h-full w-full object-contain"
          loading="eager"
        >
      </button>
    </div>
  </div>
</template>
